// Built-in site content. The admin portal can override any section (Firestore site_content/main);
// a section that was never edited, or was reset, shows these defaults.
import { PLAYBOOK_DESCRIPTIONS } from './mockPlaybooks';

// Answers may be one string or a list of paragraphs; everything else uses lists
const asParagraphs = (answer) => (Array.isArray(answer) ? answer : [answer]);
const normalize = (list) => list.map(({ question, answer }) => ({ question, answer: asParagraphs(answer) }));

const HOME_FAQ = [
  {
    question: "How is Odikai better?",
    answer: [
      "Odikai isn't just another pick recommendation platform.",
      "We build hundreds of strategies for betting on the UFC and test their historical performance to pick the ones that made the most money while minimizing losses. Our playbook building tool lets you use that same technology to test your own strategies and find a system that aligns with how you already place bets.",
      "It's similar to picking stocks for a portfolio, and we've adapted our systems from the same tools that investors use to make long-term money in the stock market.",
      "Every boxer doesn't need to win by KO - sometimes a split decision is all it takes to get the win. That's how we approach betting: winning is always better than losing, even if it isn't flashy."
    ]
  },
  {
    question: "How is Odikai different?",
    answer: [
      "We don't treat sports betting like Moneyball - we view it as any other appreciating asset.",
      "That means we're not just focused on predicting winners; we're also maximizing returns, minimizing volatility, and trading moonshot guesses for reliable, long-term growth.",
      "Our unique strategy selection method allows us to analyze each match from multiple perspectives, relying on real historical performance (not black-box AI models) to determine the best approach.",
      "The best coaches enter games armed with dozens of strategies to adapt and win, no matter the circumstances. Fighters step into the ring with a combination of refined skills and tools, all working together to get the win. So why rely on just one prediction model?"
    ]
  },
  {
    question: "How much does Odikai cost?",
    answer: [
      "To be honest, we're not sure yet! We're currently working on our subscription service.",
      "Our AI-powered picks are, and always will be, free. If you want to get notified when you can subscribe for our advanced playbook-building tools, AI-powered strategies, and more leagues, create a free account."
    ]
  },
  {
    question: "Can I place bets on Odikai?",
    answer: [
      "No.",
      "We are not a sportsbook and do not allow you to place bets directly through Odikai. Our recommended picks can be placed using any major sportsbook, and the odds we display are based on the average odds from several betting platforms."
    ]
  },
  {
    question: "What leagues does Odikai support?",
    answer: [
      "Right now, Odikai focuses exclusively on the UFC. We chose to start with a league that is both challenging and less extensively researched than others, and we believe we succeeded.",
      "We're working on building playbook building tools for other leagues (NBA, NFL, NCAA, etc.), though.",
      "Create a free account to be notified when we release new tools and be among the first to build a playbook for your favorite league."
    ]
  }
];

const FAQ_PAGE = [
  {
    question: "What's a strategy?",
    answer: "A strategy is a way of selecting fighters in the UFC that can be systematically repeated. For instance, always picking the favorite could be considered a strategy, as could only picking fighters who won their last fight and are over 6 feet tall."
  },
  {
    question: "What's a playbook?",
    answer: "A playbook is a collection of strategies. When Odikai builds your playbook, we analyze how your selected strategies performed over the last year to determine how much money should be allocated to each strategy. Then, when building picks for an event, we gather the picks for every strategy in your playbook, combine them, and provide you with the picks that have the highest weight after combining all of your strategies."
  },
  {
    question: "Why can I only pick from these specific strategies?",
    answer: "Currently, Odikai only supports this 'basic' method of playbook building. Future versions will include a more 'advanced' playbook design tool."
  },
  {
    question: "What's a favorite? What's an underdog? What do the numbers on this page mean?",
    answer: [
      "When placing a bet, you'll encounter odds that indicate the potential payout if the bet wins and the likelihood of it being correct as assessed by the sportsbooks. American odds are presented as '-400' or '+185', while decimal odds are shown as '1.25' or '2.85'.",
      "For American odds, the favorite is indicated by a minus sign (-), and the underdog by a plus sign (+). The favorite is considered more likely to win by the sportsbooks. The underdog is seen as less likely to win. For example, odds of -1000 suggest a higher likelihood of winning compared to -500 or +350.",
      "For decimal odds, the favorite has the lower number, and the underdog has the higher number. Odds of 1.10 indicate a higher likelihood of winning compared to 1.20 or 4.5.",
      "If you choose 'At least 1.25 (-400)' as your Big Favorites Strategy, that means the strategy would select fighters where their odds are at least 1.25/-400. This would include fighters with odds of 1.2/-500 but exclude those with odds of 1.5/-200."
    ]
  },
  {
    question: "What's a parlay? What's a parlay leg?",
    answer: "A parlay is a combination of bets, each referred to as a leg. A 3-leg parlay consists of three different bets, with the overall odds of the parlay determined by the odds of each leg. A parlay only pays out if all bets within it are correct."
  },
  {
    question: "What's a capper?",
    answer: "A capper is someone who creates, shares, or sells betting picks. Technically, anyone who bets on the UFC could be considered a capper, but the term is typically used for individuals who sell or share their picks publicly. We model our UFC cappers based on their publically available picks and use AI to generate unique names that reflect their approach to choosing winners."
  },
  {
    question: "Why can my total wager be less than my budget?",
    answer: "The total wager is determined by the bets generated from the included strategies and is based on how we build and test our playbooks. To maximize returns, we recommend betting the suggested total wager rather than increasing your budget to match how much you may want to spend. If it's optimal for a playbook to bet your entire budget, this will be reflected in the total wager."
  }
];
export const DEFAULT_SITE_CONTENT = {
  banner: { enabled: false, text: '', link_label: '', link_url: '' },
  hero: {
    title: 'Bet like an investor.',
    subtitle: 'Beat the sportsbooks with the tech that powers Wall Street.',
  },
  home_faq: normalize(HOME_FAQ),
  faq: normalize(FAQ_PAGE),
  playbook_descriptions: { ...PLAYBOOK_DESCRIPTIONS },
};

export const SITE_CONTENT_SECTIONS = Object.keys(DEFAULT_SITE_CONTENT);
